<template>
    <base-modal
        :visible="true"
        :show_header="false"
        :padding="false"
        min-height="35rem"
        @close="$emit('close')"
        class="notes-modal">

        <div class="columns wrapper">
            <div class="column is-narrow topics scroll">
                <b>Filter by Topic</b><br/><br/>

                <div @click="select_topic(null)" :class="{'is-active': !topic_id}" class="topic">
                    <i class="fas fa-copy"></i>&nbsp;
                    All Topics
                </div>

                <div v-for="topic in topics.data"
                     @click="select_topic(topic.id)"
                     :key="topic.id"
                     :class="{'is-active': topic.id === topic_id}"
                     class="topic">

                    <i class="fas fa-copy"></i>&nbsp;
                    {{topic.name}}
                </div>
            </div>

            <div class="column notes-list">
                <div>
                    <b>Select note</b>
                    <i @click="$emit('close')" class="fas fa-times is-pulled-right close"></i>

                    <note-list-sort @select="sort" class="action"/>

                    <tag-picker
                        :space="space"
                        :exclude="tag_ids"
                        @select="select_tag"
                        class="action">
                        <i class="fas fa-tag"></i>
                    </tag-picker>

                    <base-input
                        v-model="search_query"
                        @input="search"
                        name="search"
                        class="is-pulled-right"
                        input_class="is-small"
                        placeholder="Search..."
                        icon="fas fa-search"/>

                    <div class="is-clearfix"></div>

                    <div v-if="selected_tags.length" class="tags">
                        <div v-for="tag in selected_tags" :key="tag.id" class="tag">
                            {{tag.name}} &nbsp;
                            <i @click="remove_tag(tag)" class="fas fa-times pointer"></i>
                        </div>
                    </div>
                </div>


                <div class="notes scroll" ref="notes">
                    <base-button
                        text="Create new Note"
                        icon="fas fa-plus"
                        class="is-block is-light"
                        @click="$emit('create')"/>

                    <div v-if="notes.data.results && notes.data.results.length === 0">
                        Notes not found.
                    </div>

                    <a v-for="note in notes.data.results"
                       @click.stop="select_note(note)"
                       @dblclick="add_one(note)"
                       :key="note.id"
                       :class="{'is-active': selected_notes.includes(note.id)}"
                       class="box note">

                        <b>{{note.name}}</b>

                        <div class="has-text-grey-light">
                            <div class="is-pulled-left">
                                {{note.topic ? note.topic.name : 'Private Notes'}}
                            </div>

                            <base-datetime :date="note.updated_at" class="is-pulled-right"/>

                            <div class="is-clearfix"></div>
                        </div>
                    </a>

                    <mugen-scroll
                        :should-handle="!notes.loading && !last_page"
                        :handler="load"
                        scroll-container="notes"
                        ref="scroll">
                        <p v-if="!last_page">loading...</p>
                    </mugen-scroll>
                </div>

                <div>
                    <div class="buttons is-pulled-right">
                        <base-button
                            v-if="selected_notes.length"
                            text="Add selected"
                            icon="fas fa-check"
                            class="is-primary"
                            @click="add_notes"/>

                        <base-button
                            text="Close"
                            class="is-danger"
                            icon="fas fa-times"
                            @click="$emit('close')"/>
                    </div>
                </div>
            </div>
        </div>
    </base-modal>
</template>

<script>
import { mapState } from 'vuex';
import _xor from 'lodash/xor';
import _map from 'lodash/map';
import _reject from 'lodash/reject';
import MugenScroll from 'vue-mugen-scroll';
import debounce from 'lodash/debounce';
import NoteListSort from '../note/NoteListSort.vue';
import TagPicker from '../tag/TagPicker.vue';


export default {
    name: 'CardCreateModal',
    props: ['space', 'flow', 'column'],
    components: {
        TagPicker,
        NoteListSort,
        MugenScroll,
    },
    computed: {
        ...mapState('topics', { topics: 'list' }),
        ...mapState('notes', { notes: 'list' }),
        tag_ids() {
            return _map(this.selected_tags, 'id');
        },
    },
    data() {
        return {
            page: 1,
            order: '-created_at',
            last_page: false,
            topic_id: null,
            search_query: '',
            selected_notes: [],
            selected_tags: [],
        };
    },
    methods: {
        async load(search = null) {
            await this.$store.dispatch('notes/list', {
                ordering: this.order,
                tags: this.tag_ids,
                is_private: false,
                space_id: this.space.id,
                topics: this.topic_id ? [this.topic_id] : [],
                extend: this.page > 1,
                page: this.page,
                has_not_card: true,
                hide_private: true,
                search,
            });

            this.page += 1;
            this.last_page = !this.notes.data.next;
        },
        reload(search = null) {
            this.page = 1;
            this.last_page = false;
            this.load(search);
        },
        select_topic(id) {
            this.topic_id = id;
            this.reload();
        },
        search: debounce(function (query) {
            this.reload(query);
        }, 500),
        select_note(note) {
            // Toggle element in array
            this.selected_notes = _xor(this.selected_notes, [note.id]);
        },
        add_one(note) {
            this.selected_notes = [note.id];
            this.add_notes();
        },
        async add_notes() {
            const data = { notes: this.selected_notes };
            if (['open', 'close'].includes(this.column.id)) {
                data.is_open = this.column.id === 'open';
            } else {
                data.column = this.column.id;
            }

            await this.$store.dispatch('cards/create', {
                space_id: this.space.id,
                flow_id: this.flow.id,
                data,
            });

            await this.$store.dispatch('cards/list', {
                space_id: this.space.id,
                flow_id: this.flow.id,
                column: this.column.id,
            });

            this.selected_notes = [];
            this.$emit('close');
        },
        select_tag(tag) {
            this.selected_tags.push(tag);
            this.reload(this.search_query);
        },
        remove_tag(tag) {
            this.selected_tags = _reject(this.selected_tags, { id: tag.id });
            this.reload(this.search_query);
        },
        sort(sort) {
            this.order = sort;
            this.reload(this.search_query);
        },
    },
};
</script>

<style scoped>
    .notes-modal >>> .modal-card-body {
        overflow: hidden;
    }

    .notes-list {
        display: flex;
        flex-direction: column;
    }

    .wrapper {
        height: 35rem;
        padding: 0;
        margin: 0;
    }

    .topics {
        background: #f5f5f5;
        width: 35%;
    }

    .topic {
        padding: 0.4rem 0.7rem;
    }

    .topic:hover, .topic.is-active {
        background: #cbcbcb;
        border-radius: 0.2rem;
        cursor: pointer;
    }

    .close {
        margin: 0.3rem 0 0 1rem;
        font-size: 1.3em;
        cursor: pointer;
    }

    .notes {
        height: calc(100% - 5.5rem);
        padding: 0.4rem;
        margin-bottom: 0.6rem;
        border-bottom: 1px solid #f1f1f1;
        border-top: 1px solid #f1f1f1;
    }

    .notes > .note {
        padding: 0.4rem 0.7rem;
        margin-bottom: 0.7rem;
    }

    .add-selected {
        margin-left: 0.4rem;
    }

    .action {
        margin-left: 1rem;
        float: right;
    }

    .tags{
        margin-bottom: 0;
    }
</style>
